import { useNavigate, useParams } from "react-router-dom";
import StaffDetailHeader from "components/staff/StaffDetailHeader/StaffDetailHeader";
import {
  BasicInfoSection,
  BasicInfoSectionRef,
} from "components/staff/BasicInfoSection/BasicInfoSection";
import {
  ContactEmergencySection,
  ContactEmergencySectionRef,
} from "components/staff/ContactEmergencySection/ContactEmergencySection";
import {
  IdentityTaxSection,
  IdentityTaxSectionRef,
} from "components/staff/IdentityTaxSection/IdentityTaxSection";
import {
  InsuranceSection,
  InsuranceSectionRef,
} from "components/staff/InsuranceSection/InsuranceSection";
import {
  DriverLicenseSection,
  DriverLicenseSectionRef,
} from "components/staff/DriverLicenseSection/DriverLicenseSection";
import {
  AccountSection,
  AccountSectionRef,
} from "components/staff/AccountSection/AccountSection";
import { useRef, useState } from "react";
import showAlertNotification from "components/common/Modal/AlertNotification";
import { StaffState } from "types/staff-edit.types";
import { useCreateStaffMutation } from "services/staff.service";
import { useCreateDriverMutation } from "services/driver.service";
import { FileData, ImagePayload } from "types/image.type";

const emptyData: StaffState = {
  accountInfo: {
    fullName: "",
    email: "",
    password: "",
    isActive: true,
    avatarImage: [] as ImagePayload[],
  },
  basicInfo: {
    birthDate: "",
    nation: "",
    gender: "",
    ethnicity: "",
    maritalStatus: "",
    bankAccount: { accountNumber: "", bankName: "", bankLogo: [] },
  },
  contactInfo: {
    phoneNumber: "",
    homePhone: "",
    currentAddress: "",
    permanentAddress: "",
  },
  emergencyInfo: { nationalityPhone: "", relationship: "" },
  identityTax: {
    identityNumber: "",
    taxCode: "",
    issuedDate: "",
    expiredDate: "",
    issuedPlace: "",
    identityImages: [],
  },
  insuranceInfo: { socialInsuranceNumber: "", insuranceImages: [] },
  driverLicense: {
    licenseClass: "",
    licenseNumber: "",
    licenseExpiredDate: "",
    vehicle: "",
    frontLicenseImages: [],
    backLicenseImages: [],
  },
};

const StaffAddPage = () => {
  const { type } = useParams<{ type: string }>();
  const navigate = useNavigate();
  const [formData, setFormData] = useState<StaffState>(emptyData);
  const [headerData, setHeaderData] = useState({
    fullName: "",
    role: "",
    staffCode: "",
    phoneNumber: "",
    avatarImage: [] as ImagePayload[],
  });

  const [createStaff] = useCreateStaffMutation();
  const [createDriver] = useCreateDriverMutation();

  // Refs
  const accountRef = useRef<AccountSectionRef>(null);
  const basicInfoRef = useRef<BasicInfoSectionRef>(null);
  const contactRef = useRef<ContactEmergencySectionRef>(null);
  const identityRef = useRef<IdentityTaxSectionRef>(null);
  const insuranceRef = useRef<InsuranceSectionRef>(null);
  const driverRef = useRef<DriverLicenseSectionRef>(null);

  const getAvatarString = (images: ImagePayload[]): string => {
    if (!images || images.length === 0) return "";
    const firstImage = images[0];
    return (
      typeof firstImage === "string"
        ? firstImage
        : (firstImage as { filePath?: string }).filePath || ""
    ) as string;
  };

  const handleUpdate = (
    path: string,
    value: string | FileData[] | ImagePayload[],
  ) => {
    if (path.startsWith("header.")) {
      const key = path.split(".")[1];
      setHeaderData((prev) => ({ ...prev, [key]: value }));
      return;
    }

    setFormData((prev: StaffState) => {
      const newObj: StaffState = { ...prev };
      let current: Record<string, unknown> = newObj as unknown as Record<
        string,
        unknown
      >;
      const keys = path.split(".");
      const lastKey = keys[keys.length - 1];

      for (let i = 0; i < keys.length - 1; i++) {
        const key = keys[i];
        if (!current[key]) current[key] = {};
        const nextLevel = current[key] as Record<string, unknown>;
        current[key] = { ...nextLevel };
        current = current[key] as Record<string, unknown>;
      }

      current[lastKey] = value;
      return newObj;
    });
  };

  const handleSave = async () => {
    const checks = [
      basicInfoRef.current?.validateAll(),
      contactRef.current?.validateAll(),
      identityRef.current?.validateAll(),
      insuranceRef.current?.validateAll(),
    ];

    if (type === "driver") {
      checks.push(accountRef.current?.validateAll());
      checks.push(driverRef.current?.validateAll());
    }

    const results = await Promise.all(checks);
    console.log("👉 Kết quả Validate của các section:", results);
    // const isFormValid = results.every((result) => result === true);

    try {
      const genderMap: Record<string, string> = {
        Nam: "male",
        Nữ: "female",
        Khác: "other",
      };

      const finalPassword =
        type === "driver" && formData.accountInfo?.password
          ? formData.accountInfo.password
          : "123456";

      const avatarUrl = getAvatarString(headerData.avatarImage);

      const userPayload = {
        password: finalPassword,
        name: formData.accountInfo.fullName || "Nhân viên mới",
        email: formData.accountInfo.email || undefined,
        avatarImage: avatarUrl || undefined,
        phoneNumber: formData.contactInfo.phoneNumber || undefined,
        address: formData.contactInfo.currentAddress || undefined,
        birthday: formData.basicInfo.birthDate || undefined,
        permanentAddress: formData.contactInfo.permanentAddress || undefined,
        gender: formData.basicInfo.gender
          ? genderMap[formData.basicInfo.gender]
          : undefined,
        nation: formData.basicInfo.nation || undefined,
        ethnicity: formData.basicInfo.ethnicity || undefined,
        maritalStatus: formData.basicInfo.maritalStatus || undefined,
        citizenId: formData.identityTax.identityNumber || undefined,
        citizenIdDate: formData.identityTax.issuedDate || undefined,
        citizenIdPlace: formData.identityTax.issuedPlace || undefined,
        socialInsuranceNumber:
          formData.insuranceInfo.socialInsuranceNumber || undefined,
        taxCode: formData.identityTax.taxCode || undefined,
        homePhone: formData.contactInfo.homePhone || undefined,
        emergencyPhone: formData.emergencyInfo.nationalityPhone || undefined,
        emergencyRelationship: formData.emergencyInfo.relationship || undefined,
        bankAccountNumber:
          formData.basicInfo.bankAccount.accountNumber || undefined,
        bankName: formData.basicInfo.bankAccount.bankName || undefined,
      };

      if (type === "driver") {
        await createDriver({
          user: userPayload,
          driver: {
            licenseNumber: formData.driverLicense.licenseNumber || "N/A",
            licenseClass: formData.driverLicense.licenseClass || undefined,
            licenseExpiryDate:
              formData.driverLicense.licenseExpiredDate || undefined,
            mainVehicleType: formData.driverLicense.vehicle || undefined,
          },
        }).unwrap();
      } else {
        await createStaff({
          user: userPayload,
          positionTitle: headerData.role || undefined,
        }).unwrap();
      }

      showAlertNotification({
        type: "success",
        title: "Thành công",
        message: `Đã tạo ${type === "driver" ? "tài xế" : "nhân viên"} mới!`,
        duration: 3,
      });

      navigate("/settings/staff");
    } catch (error: unknown) {
      let errorMsg = "Có lỗi xảy ra, vui lòng kiểm tra lại!";

      const err = error as {
        data?: {
          message?: string | string[];
          error?: string;
        };
        message?: string;
      };

      const backendMessage =
        err?.data?.message || err?.data?.error || err?.message;

      if (backendMessage) {
        // Trường hợp NestJS (class-validator) quăng ra một mảng nhiều lỗi
        if (Array.isArray(backendMessage)) {
          errorMsg = backendMessage.join(", ");
        }
        // Trường hợp NestJS quăng ra câu lỗi trùng lặp dữ liệu mà bác vừa setup
        else if (typeof backendMessage === "string") {
          errorMsg = backendMessage;
        }
      }

      showAlertNotification({
        type: "error",
        title: "Lỗi tạo mới",
        message: errorMsg,
        duration: 5,
      });
    }
  };

  return (
    <div className="flex flex-col h-full">
      <StaffDetailHeader
        fullName={headerData.fullName || "Nhân viên mới"}
        role={headerData.role || (type === "driver" ? "Tài xế" : "Nhân viên")}
        staffCode={headerData.staffCode || "---"}
        phoneNumber={headerData.phoneNumber || "---"}
        department=""
        avatarImage={getAvatarString(headerData.avatarImage)}
        showOnlySave={true}
        onSave={handleSave}
      />

      <div className="mx-6 my-6 py-4 px-6 bg-white rounded-2xl shadow-sm">
        {type === "driver" && (
          <AccountSection
            ref={accountRef}
            data={formData.accountInfo}
            onUpdate={handleUpdate}
          />
        )}

        <BasicInfoSection
          ref={basicInfoRef}
          data={formData.basicInfo}
          onUpdate={handleUpdate}
        />
        <ContactEmergencySection
          ref={contactRef}
          contact={formData.contactInfo}
          emergency={formData.emergencyInfo}
          onUpdate={handleUpdate}
        />
        <IdentityTaxSection
          ref={identityRef}
          data={formData.identityTax}
          onUpdate={handleUpdate}
        />
        <InsuranceSection
          ref={insuranceRef}
          data={formData.insuranceInfo}
          onUpdate={handleUpdate}
        />

        {type === "driver" && (
          <DriverLicenseSection
            ref={driverRef}
            data={formData.driverLicense}
            onUpdate={handleUpdate}
          />
        )}
      </div>
    </div>
  );
};

export default StaffAddPage;
